import {
  CircularProgress,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  Typography,
} from "@material-ui/core";
import { Error, Warning } from "@material-ui/icons";

import { format, parseISO } from "date-fns";
import { forwardRef, useEffect, useState } from "react";
import { useTranslate } from "react-admin";
import { handleAxiosError } from "../../../../common/data/axiosErrorHandler";
import {
  DvirFormAreaPointSeverity,
  MaintenanceDashboardApiFactory,
  VehicleDvirDefectDto,
} from "../../../../common/data/service-proxies";
import { useAxiosInstance } from "../../../../common/hooks/useAxiosInstance";
import { useTableHeaderStyles } from "../common";
import { Empty } from "../widgets/Empty";
import { Widget } from "../widgets/Widget";
import { VehicleWidgetProps } from "./VehicleWidgetProps";
import "./UnresolvedVehicleDvirDefects.css";
import { useHistory } from "react-router-dom";
import classNames from "classnames";
import resourceNames from "../../../../common/data/resourceNames";

const useStyles = makeStyles((theme: Theme) => ({
  warning: {
    color: theme.palette.warning.main,
  },
  error: {
    color: theme.palette.error.main,
  },
}));

export const UnresolvedVehicleDvirDefects = forwardRef(
  ({ className, icon, title, vehicle, ...rest }: VehicleWidgetProps, ref) => {
    const translate = useTranslate();
    const history = useHistory();
    const [loading, setLoading] = useState<boolean>(true);
    const [data, setData] = useState<VehicleDvirDefectDto[]>();
    const axios = useAxiosInstance();
    const classes = useTableHeaderStyles();
    const componentClasses = useStyles();

    useEffect(() => {
      if (!axios) {
        setLoading(false);
        return;
      }

      if (!vehicle?.id) {
        setLoading(false);
        return;
      }

      setLoading(true);
      MaintenanceDashboardApiFactory(undefined, axios.defaults.baseURL, axios)
        .apiAppMaintenancedashboardDvirdefectsforvehicleVehicleIdGet(vehicle.id)
        .then((result) => {
          setData(result.data);
        })
        .catch((e) => console.error(handleAxiosError(e)))
        .finally(() => setLoading(false));
    }, [axios, vehicle]);

    return (
      <Widget
        title={title}
        icon={icon}
        className={className}
        ref={ref}
        {...rest}
      >
        <div className="categories">
          {loading ? (
            <div className="category">
              <CircularProgress size="3rem" />
            </div>
          ) : data && data.length ? (
            <div className="table-wrapper">
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow className={classes.tableHead}>
                      <TableCell>Date</TableCell>
                      <TableCell>Severity</TableCell>
                      <TableCell>Area</TableCell>
                      <TableCell>Point</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.map((x) => (
                      <TableRow
                        key={x.id}
                        className={classNames(classes.row, classes.clickable)}
                        onClick={() =>
                          history.push(
                            `/${resourceNames.ASSETS_DVIR_HISTORY}/${vehicle.id}/${x.dvirReportId}`
                          )
                        }
                      >
                        <TableCell>
                          {format(parseISO(x.date!), "MMM do, yyyy")}
                        </TableCell>
                        <TableCell>
                          <div className="dvir-defect-info-cell">
                            <Typography variant="body2">
                              {translate(
                                `resources.admin-dvir-forms.severities.${x.severity}`
                              )}
                            </Typography>
                            {x.severity === DvirFormAreaPointSeverity.Major ? (
                              <div
                                className={`info-value-icon ${componentClasses.error}`}
                              >
                                <Error />
                              </div>
                            ) : (
                              <div
                                className={`info-value-icon ${componentClasses.warning}`}
                              >
                                <Warning />
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{x.area}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{x.point}</Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </div>
          ) : (
            <Empty />
          )}
        </div>
      </Widget>
    );
  }
);
